import "@fontsource-variable/instrument-sans";
import "./styles.css";
import { Component, useEffect, useRef, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { chainBySlug, tokenHref, useGet, usePing, type TokenRow } from "./api";
import { price, usd } from "./format";
import { Pulse } from "./Pulse";
import { Screener } from "./Screener";
import { Token } from "./Token";
import { Wallet } from "./Wallet";
import { A, Change, go, Icon, LogoMark, TokenLogo, usePath } from "./ui";

const PORTAL = "https://app.aurelia.build";

function Search() {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    const close = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    addEventListener("pointerdown", close);
    return () => removeEventListener("pointerdown", close);
  }, []);
  const { data, loading } = useGet<{ tokens: TokenRow[] }>(debounced ? `/tokens?q=${encodeURIComponent(debounced)}&limit=8` : null);
  const rows = data?.tokens ?? [];
  const pick = (t: TokenRow) => {
    setOpen(false);
    setQ("");
    go(tokenHref(t.chain, t.token.address));
  };
  return (
    <div className="search" ref={box}>
      <label className="search-field">
        <Icon name="search" size={16} />
        <input
          value={q}
          placeholder="Search by name, symbol or address"
          aria-label="Search tokens"
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setSel(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") setSel((s) => Math.min(s + 1, rows.length - 1));
            else if (e.key === "ArrowUp") setSel((s) => Math.max(s - 1, 0));
            else if (e.key === "Enter" && rows[sel]) pick(rows[sel]);
            else if (e.key === "Escape") setOpen(false);
            else return;
            e.preventDefault();
          }}
        />
      </label>
      {open && debounced && (
        <div className="search-results" role="listbox">
          {rows.map((t, i) => (
            <button key={`${t.chain}:${t.token.address}`} role="option" aria-selected={i === sel} className="search-row" onPointerEnter={() => setSel(i)} onClick={() => pick(t)}>
              <TokenLogo src={t.token.logo} symbol={t.token.symbol} chain={t.chain} size={30} />
              <span className="search-name">
                <strong>{t.token.symbol ?? "?"}</strong>
                <span className="muted">{t.token.name}</span>
              </span>
              <span className="search-figures">
                <span className="num">{price(t.priceUsd)}</span>
                <span className="muted num">Vol {usd(t.volume24hUsd)}</span>
              </span>
              <Change value={t.change["24h"]} />
            </button>
          ))}
          {!rows.length && <p className="search-empty muted">{loading ? "Searching…" : "No token matches."}</p>}
        </div>
      )}
    </div>
  );
}

function Header() {
  const path = usePath();
  const ping = usePing();
  return (
    <header className="header">
      <A href="/" className="brand" title="Aurelia Screener">
        <LogoMark size={26} />
      </A>
      <nav className="nav">
        <A href="/" className={path === "/" ? "nav-link active" : "nav-link"}>
          Screener
        </A>
        <A href="/pulse" className={path === "/pulse" ? "nav-link active" : "nav-link"}>
          Pulse
        </A>
      </nav>
      <Search />
      <span className={`ping num${ping === null ? "" : ping < 200 ? " fast" : " slow"}`} title="Round trip from your browser to this site's server">
        {ping === null ? "–" : `${ping} ms`}
      </span>
      <a className="pill" href={`${PORTAL}/#/login`}>
        Get an API key
      </a>
    </header>
  );
}

function Footer() {
  return (
    <footer className="footer">
      <span className="footer-brand">
        <LogoMark size={18} />
        Aurelia Screener
      </span>
      <span className="muted">
        Every figure on this site comes from the{" "}
        <a href={`${PORTAL}/#/docs`}>Aurelia API</a>, read with an ordinary API key. Not financial advice.
      </span>
    </footer>
  );
}

function NotFound() {
  useEffect(() => void (document.title = "Not found · Aurelia Screener"), []);
  return (
    <div className="empty-page">
      <h1>Nothing here</h1>
      <p className="muted">
        <A href="/">Back to the screener</A>
      </p>
    </div>
  );
}

// A page that throws says so under the header instead of blanking the site; another path starts clean.
class PageGuard extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="empty-page">
        <h1>This page could not be shown</h1>
        <p className="muted">
          <a href={location.pathname}>Try again</a> or <A href="/">go back to the screener</A>
        </p>
      </div>
    );
  }
}

function App() {
  const path = usePath();
  const [, slug, address, extra] = path.split("/");
  const chain = slug ? chainBySlug(slug) : undefined;
  let page;
  if (path === "/") page = <Screener />;
  else if (path === "/pulse") page = <Pulse />;
  else if (chain && address === "wallet" && extra) page = <Wallet key={path} chain={chain} address={extra} />;
  else if (chain && address && extra === undefined) page = <Token key={path} chain={chain} address={address} />;
  else page = <NotFound />;
  return (
    <>
      <Header />
      <main className="main">
        <PageGuard key={path}>{page}</PageGuard>
      </main>
      <Footer />
    </>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
